<template>
  <div class="candidates">
    <app-layout customToolbar centered>
      <template v-slot:body>
        <div class="container col-lg-8 col-sm-12">
          <BaseListToolbar title="People" @search="updateSearchValue">
            <template v-slot:inline-filters>
              <WorkerListFilter
                @onClearFilters="clearFilters"
                :isLoading="isLoadingCandidates"
                :availableFilters="availableFilters"
                :outerSelectedFilters="workerListFilters"
                @filter="updateFilters"
              />
            </template>
            <template v-slot:tool-right>
              <div class="float-right d-flex justify-center mb-2">
                <PrimaryButton @click.native="viewTalentPools" text>
                  <v-img
                    contain
                    src="@/assets/svg/pool_primary_icon.svg"
                    class="mr-2"
                  />
                  View Talent Pools
                </PrimaryButton>
              </div>
            </template>
          </BaseListToolbar>
          <div v-if="isLoadingCandidates">
            <v-skeleton-loader
              v-for="i in 10"
              :key="i"
              type="list-item-avatar"
            />
          </div>
          <div v-else>
            <div v-if="isNotEmpty" class="user-list">
              <v-list-item v-for="candidate in candidates" :key="candidate.id">
                <WorkerListItem
                  data-test="worker"
                  @view="() => handleView(candidate)"
                  @select="() => handleSelect(candidate)"
                  :candidate="candidate"
                  :isSelectMultiple="selectMultiple"
                  :isSelected="isSelected(candidate)"
                />
              </v-list-item>
              <v-pagination
                :total-visible="page.pageSize"
                v-if="showPagination"
                v-model="page.currentPage"
                @input="loadCandidates"
                :length="page.lastPage"
              />
            </div>
            <EmptyStates
              data-test="empty-list"
              v-else
              :description="emptyStateDescription"
              :isFiltered="isFiltered"
            />
          </div>
          <BaseFooter class="relative">
            <MultipleSelectAction
              v-if="!isMobile"
              v-show="selectMultiple"
              :selectedCount="selectedCount"
              :isSelectAll="isSelectAll"
              @selectAll="handleSelectAll"
              @cancel="handleCancel"
            />
          </BaseFooter>
          <BaseFooter>
            <ExpandedButtons v-if="selectedCount">
              <Permissioned
                :vertical="isMobile"
                section="talent-pools"
                class="actions-bar"
              >
                <NewTalentPoolWithWorkersAction
                  permission="create"
                  :users="selectedCandidates"
                />
                <AddToPoolAction
                  :selectedCandidates="selectedCandidates"
                  v-if="selectMultiple"
                  @success="handleCancel"
                />
                <NewMessageAction
                  v-if="selectedCount"
                  :selectedCandidatesIds="selectedCandidatesIds"
                  textColor="#0965dc"
                  color="normal"
                  title="Message"
                  :iconSrc="require('@/assets/svg/edit_file_blue.svg')"
                />
              </Permissioned>
            </ExpandedButtons>
          </BaseFooter>
        </div>
      </template>
    </app-layout>
  </div>
</template>

<script>
import appLayout from "@/components/common/base-layouts/AppLayout";
import BaseListToolbar from "@/components/common/Toolbar/BaseListToolbar";
import { createNamespacedHelpers } from "vuex";
import WorkerListFilter from "@/views/workers/components/WorkerListFilter";
import { size, filter, find, isEmpty, map } from "lodash";
import { isPermissioned } from "@/utils/permissions";
import WorkerListItem from "@/views/workers/components/WorkerListItem";
import EmptyStates from "@/components/common/EmptyStates";
import { GET_ALL_WORKERS } from "@/store/modules/workers/action-types";
import { WORKERS_NAMESPACE } from "@/store/modules/workers";
import { SET_WORKER_LIST_FILTERS } from "@/store/modules/workers/mutation-types";
import BaseFooter from "@/components/common/BaseFooter";
import { paginationMixin } from "@/mixins/pagination.mixin";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import MultipleSelectAction from "@/views/workers/components/actions/MultipleSelectAction";
import AddToPoolAction from "@/views/workers/components/actions/AddToPoolAction";
import { FETCH_TALENT_POOLS } from "@/store/modules/talentPools/actions";
import { TALENT_POOLS_NAMESPACE } from "@/store/modules/talentPools";
import NewTalentPoolWithWorkersAction from "@/views/workers/TalentPools/actions/NewTalentPoolWithWorkersAction";
import ExpandedButtons from "@/components/common/ExpandedButtons";
import Permissioned from "@/components/common/Permissioned";
import NewMessageAction from "@/views/conversations/actions/NewMessageAction";

const { mapActions, mapState, mapMutations } = createNamespacedHelpers(
  WORKERS_NAMESPACE
);
const { mapActions: talentPoolMapActions } = createNamespacedHelpers(
  TALENT_POOLS_NAMESPACE
);

export default {
  title: "Candidates - Track",
  name: "CandidateList",
  mixins: [paginationMixin],
  components: {
    appLayout,
    WorkerListItem,
    BaseListToolbar,
    WorkerListFilter,
    EmptyStates,
    BaseFooter,
    PrimaryButton,
    MultipleSelectAction,
    AddToPoolAction,
    ExpandedButtons,
    NewTalentPoolWithWorkersAction,
    Permissioned,
    NewMessageAction
  },
  async created() {
    this.loadCandidates();
    this.fetchTalentPools();
  },
  data() {
    return {
      search: "",
      availableFilters: {},
      selectedCandidates: []
    };
  },
  computed: {
    ...mapState({
      candidates: state => state.workers,
      isLoadingCandidates: state => state.isFetchingData,
      workerListFilters: state => state.workerListFilters
    }),
    selectedCandidatesIds() {
      return map(this.selectedCandidates, "id");
    },
    totalVisible() {
      return this.$vuetify.breakpoint.smAndDown ? 4 : 7;
    },
    requestParams() {
      return {
        filter: {
          account_type: "worker",
          ...this.workerListFilters,
          ...(size(this.search) && { name: this.search })
        },
        ...this.requestPageParams
      };
    },
    isNotEmpty() {
      return size(this.candidates);
    },
    isFiltered() {
      return Boolean(size(this.workerListFilters) || size(this.search));
    },
    emptyStateDescription() {
      return this.isFiltered
        ? "No candidates match your search criteria"
        : "No candidates have been created";
    },
    selectMultiple() {
      return !isEmpty(this.selectedCandidates);
    },
    isSelectAll() {
      return this.isNotEmpty === this.selectedCount;
    },
    selectedCount() {
      return size(this.selectedCandidates);
    }
  },
  methods: {
    ...mapActions({
      fetchWorkers: GET_ALL_WORKERS
    }),
    ...mapMutations({
      setWorkerListFilters: SET_WORKER_LIST_FILTERS
    }),
    ...talentPoolMapActions({
      fetchTalentPools: FETCH_TALENT_POOLS
    }),
    async loadCandidates() {
      const data = await this.fetchWorkers(this.requestParams);
      if (data) {
        this.adjustPageDetails(data.meta);
      }
    },
    adjustPageDetails(meta) {
      this.updatePageDetails(meta);
      this.availableFilters = meta.filters;
    },
    updateSearchValue(text) {
      this.search = text;
      this.loadCandidates();
    },
    handleView({ id }) {
      this.$router.push({
        name: "workerTabs",
        params: { id }
      });
    },
    handleSelect({ id }) {
      const isSelected = this.isSelected({ id });
      isSelected
        ? (this.selectedCandidates = filter(
            this.selectedCandidates,
            selectedCandidate => selectedCandidate.id !== id
          ))
        : this.selectedCandidates.push({ id });
    },
    isSelected({ id }) {
      return Boolean(
        find(
          this.selectedCandidates,
          selectedCandidate => selectedCandidate.id === id
        )
      );
    },
    handleSelectAll() {
      this.selectedCandidates = map(this.candidates, candidate => {
        return { id: candidate.id };
      });
    },
    handleCancel() {
      this.selectedCandidates = [];
    },
    updateFilters(filters) {
      this.setWorkerListFilters(filters);
      this.resetPagination();
      this.loadCandidates();
    },
    clearFilters() {
      this.setWorkerListFilters({});
      this.resetPagination();
      this.loadCandidates();
    },
    isPermissioned,
    viewTalentPools() {
      this.$router.push({ name: "talentPools" });
    }
  }
};
</script>

<style lang="scss"></style>
